import { useCallback, useState } from 'react'
import { wordPressHost } from '../utils/location'
import { useRequestLineSubscription } from './apis/wp/useRequestLineSubscription'

interface SubscribeToLineOptions {
  onError?: () => void
}

export function useLineSubscription() {
  const { trigger } = useRequestLineSubscription()
  const [isLoading, setIsLoading] = useState(false)

  const subscribeToLine = useCallback(async (
    redirectUrl: string,
    isSubscribed = true,
    options?: SubscribeToLineOptions,
  ) => {
    try {
      setIsLoading(true)
      const queryParam = isSubscribed ? 'lineSubscription=true' : 'lineUnsubscribed=true'
      const fallbackParam = isSubscribed ? 'lineSubscription=false' : 'lineUnsubscribed=false'

      const { data } = await trigger({
        isSubscribed,
        redirectUrl: `${redirectUrl}?${queryParam}`,
        fallbackUrl: `${redirectUrl}?${fallbackParam}`,
      })
      window.location.href = data.data.url
    }
    catch (e) {
      setIsLoading(false)
      options?.onError?.()
      console.error(e)
    }
  }, [trigger])

  const subscribeLine = useCallback((options?: SubscribeToLineOptions) => {
    return subscribeToLine(`${wordPressHost}/my-account/edit-account/`, true, options)
  }, [subscribeToLine])

  const subscribeLineWithRestockNotification = useCallback((options?: SubscribeToLineOptions) => {
    return subscribeToLine(`${wordPressHost}/my-account/restock-notification/`, true, options)
  }, [subscribeToLine])

  /**
   * LINE 邀請頁專用方法
   * 成功：跳到 targetUrl 並加上 lineInvitationSubscription=true
   * 失敗：回到邀請頁並加上 lineInvitationSubscription=false & redirect={targetUrl}
   */
  const subscribeLineFromInvitation = useCallback(async (
    targetUrl: string,
    options?: SubscribeToLineOptions,
  ) => {
    try {
      setIsLoading(true)

      const successUrl = new URL(targetUrl, window.location.origin)
      successUrl.searchParams.set('lineInvitationSubscription', 'true')

      const failureUrl = new URL('/my-account/line-binding-invitation', window.location.origin)
      failureUrl.searchParams.set('lineInvitationSubscription', 'false')
      failureUrl.searchParams.set('redirect', targetUrl)

      const { data } = await trigger({
        isSubscribed: true,
        redirectUrl: successUrl.toString(),
        fallbackUrl: failureUrl.toString(),
      })

      window.location.href = data.data.url
    }
    catch (e) {
      setIsLoading(false)
      options?.onError?.()
      console.error(e)
    }
  }, [trigger])

  return {
    subscribeToLine,
    subscribeLine,
    subscribeLineWithRestockNotification,
    subscribeLineFromInvitation,
    isLoading,
  }
}
